import { useEffect, useState } from "react"
import { LogApi } from "../../api/LogApi"
import { IRunEvent } from "../../interface/IRunEvent"
import Skeleton from '@mui/material/Skeleton';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import { alpha, useTheme } from '@mui/material/styles';

import moment from 'moment'
import { LogTimeRange } from "../../components/LogTimeRange";
import { OrderDetailsModal } from "../../components/modals/OrderDetails/OrderDetailsModal";
import { NoLogs } from "../../components/placeholder/NoLogs";
import { LogsTable } from "./LogsTable";
import { dashboardMutedTextSx } from "../dashboardv2/dashboardStyles";

export const Logs = () =>
{
  const theme = useTheme()

  const [ logs, setLogs ] = useState<IRunEvent[] | undefined>([])
  const [ loading, setLoading ] = useState(false)

  const [ order, setOrder ] = useState<"asc" | "desc">("desc")
  const [ limit, setLimit ] = useState(10000)
  const [ skip, setSkip ] = useState(0)
  const [ fromTime, setFromTime ] = useState(48)
  const [ showOrderDetails, setShowOrderDetails ] = useState<IRunEvent | undefined>(undefined)


  const getLogs = async () =>
  {
    setLoading(true)
    const logApi = new LogApi()
    const logData = await logApi.getRunEvents(order, limit, skip, moment().subtract(fromTime, "hours").toISOString());
    setLogs(logData)
    setLoading(false)
  }

  useEffect(() => {

    getLogs()

  }, [ fromTime ])

  const getEventSummary = () => {
    const items = logs || []
    const buys = items.filter(item => item.didBuy).length
    const sells = items.filter(item => item.didSell).length
    const realisedProfit = items.reduce((acc, item) => acc + (Number(item.profit) || 0), 0)
    return { buys, sells, realisedProfit }
  }

  const summary = getEventSummary()


  return(
    <Box
      sx={{
        minHeight: "91vh",
        overflow: "auto",
        m: { xs: 1.5, md: 2 },
        p: { xs: 2, md: 3 },
        borderRadius: "20px",
        position: "relative",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, #06111f 0%, #091829 58%, #050c16 100%)`
            : `linear-gradient(180deg, #f3f8ff 0%, #ebf3ff 45%, #ffffff 100%)`,
        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            theme.palette.mode === "dark"
              ? `radial-gradient(circle at top left, ${alpha("#22d3ee", 0.16)}, transparent 24%), radial-gradient(circle at top right, ${alpha("#14b8a6", 0.12)}, transparent 28%)`
              : `radial-gradient(circle at top left, ${alpha("#60a5fa", 0.18)}, transparent 24%), radial-gradient(circle at top right, ${alpha("#22c55e", 0.1)}, transparent 30%)`,
        }}
      />

      <CssBaseline />

      {
        loading ? 
        <Box sx={{ position: "relative", zIndex: 1 }}>
              <Skeleton height={80} />
              {
                [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i, k) => <Skeleton key={k} height={60} />)
              }

        </Box> : 
      <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }}>
        <Grid item xs={12}>
          <Box sx={styles.hero(theme)}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} lg={7}>
                <Typography variant="overline" sx={{ letterSpacing: 2, ...dashboardMutedTextSx(theme) }}>
                  Run Events
                </Typography>
                <Typography sx={{ mt: 1, fontSize: { xs: 34, md: 50 }, lineHeight: 0.98, maxWidth: 720 }}>
                  Review bot activity, fills, errors, and realised outcomes from one event timeline.
                </Typography>
                <Typography sx={{ mt: 2, maxWidth: 620, fontSize: 16, ...dashboardMutedTextSx(theme) }}>
                  Filter by time range, inspect event reasons, and drill into order details.
                </Typography>
                <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`${logs?.length || 0} events`} sx={styles.heroChip(theme)} />
                  <Chip label={`${summary.buys} buys / ${summary.sells} sells`} sx={styles.heroChip(theme)} />
                  <Chip label={`P&L $${summary.realisedProfit.toFixed(2)}`} sx={styles.heroChip(theme)} />
                </Box>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.metricCard(theme)}>
                      <ReceiptLongRoundedIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Timeline</Typography>
                      <Typography sx={{ fontSize: 22 }}>Bot run events</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.metricCard(theme)}>
                      <SyncAltRoundedIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Window</Typography>
                      <Typography sx={{ fontSize: 22 }}>Last {fromTime} hours</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={12}>
                    <Box sx={styles.metricCard(theme)}>
                      <TrendingUpRoundedIcon fontSize="small" />
                      <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Realised P&L</Typography>
                      <Typography sx={{ fontSize: 22 }}>${summary.realisedProfit.toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={styles.section(theme)}>
            <Typography variant="overline" sx={{ letterSpacing: 1.5, ...dashboardMutedTextSx(theme) }}>
              Filters
            </Typography>
            <Typography sx={{ fontSize: 28, lineHeight: 1.15, mb: 2 }}>Adjust the event window and inspect order outcomes</Typography>
            <LogTimeRange fromTime={fromTime} setFromTime={setFromTime} />
          </Box>
        </Grid>

        <Grid item xs={12}>
          { logs?.length === 0 && !loading && <NoLogs />}

          { logs && logs.length > 0 && <LogsTable logs={logs} setShowOrderDetails={setShowOrderDetails} /> }
        </Grid>

        { showOrderDetails && showOrderDetails?.marketId && showOrderDetails?.orderId && <OrderDetailsModal open={!!showOrderDetails} handleClose={() => setShowOrderDetails(undefined)} marketId={showOrderDetails?.marketId} orderId={showOrderDetails.orderId} eventTimestamp={showOrderDetails.timestamp} eventPrice={showOrderDetails.lastPrice} eventSide={showOrderDetails.didBuy ? "BUY" : showOrderDetails.didSell ? "SELL" : undefined} /> }
      </Grid>
      }
    </Box>
  )
}

const styles = {
  hero: (theme: any) => ({
    p: { xs: 2.25, md: 3.5 },
    borderRadius: "16px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#102946", 0.94)} 0%, ${alpha("#081320", 0.96)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.94)} 0%, ${alpha("#e6f0ff", 0.92)} 100%)`,
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 22px 60px ${alpha("#01050d", 0.45)}`
        : `0 22px 60px ${alpha("#9db3d1", 0.2)}`,
  }),
  metricCard: (theme: any) => ({
    p: 2,
    borderRadius: "12px",
    display: "grid",
    gap: 0.5,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.5),
  }),
  heroChip: (theme: any) => ({
    borderRadius: "999px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.68),
    color: theme.palette.text.primary,
  }),
  section: (theme: any) => ({
    p: { xs: 2, md: 2.5 },
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.58),
  }),
}
